import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
  FormControl, CircularProgress, Alert, Checkbox, FormControlLabel,
  Switch, Snackbar, Tooltip, Autocomplete, Chip,
  Accordion, AccordionSummary, AccordionDetails, InputAdornment, IconButton,
  Divider, TablePagination
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SyncIcon from '@mui/icons-material/Sync';

// Opciones predefinidas para la categoría fiscal
const TAX_CATEGORY_OPTIONS = [
  { value: 'services', label: 'services — Servicios generales', group: 'Categorías Genéricas' },
  { value: 'purchases', label: 'purchases — Compras de bienes/mercancía', group: 'Categorías Genéricas' },
  { value: 'honorarios', label: 'honorarios — Honorarios profesionales', group: 'Categorías Genéricas' },
  { value: 'arrendamientos', label: 'arrendamientos — Arrendamientos inmuebles', group: 'Categorías Genéricas' },
  { value: 'transporte_carga', label: 'transporte_carga — Transporte de carga', group: 'Transporte' },
  { value: 'transporte_pasajeros', label: 'transporte_pasajeros — Transporte pasajeros', group: 'Transporte' },
  { value: 'transporte_aereo', label: 'transporte_aereo — Transporte aéreo/marítimo', group: 'Transporte' },
  { value: 'contratos_construccion', label: 'contratos_construccion — Construcción', group: 'Específicas' },
  { value: 'consultoria_obras', label: 'consultoria_obras — Consultoría obras', group: 'Específicas' },
  { value: 'consultoria_ingenieria', label: 'consultoria_ingenieria — Ingeniería', group: 'Específicas' },
  { value: 'servicios_hoteles', label: 'servicios_hoteles — Hoteles/restaurantes', group: 'Específicas' },
  { value: 'servicios_vigilancia', label: 'servicios_vigilancia — Vigilancia/aseo (AIU)', group: 'Específicas' },
  { value: 'servicios_temporales', label: 'servicios_temporales — Empresas temporales (AIU)', group: 'Específicas' },
  { value: 'servicios_software', label: 'servicios_software — Software/web', group: 'Específicas' },
  { value: 'rendimientos_financieros', label: 'rendimientos_financieros — Rendimientos', group: 'Específicas' },
];

const FiscalRow = React.memo(({ 
  mapRow, 
  accountDescription, 
  retentionConcepts, 
  localTaxCategory, 
  isSelected, 
  onSelect, 
  onToggle, 
  onConceptChange, 
  onCategoryChange,
  onVatAccountChange,
  chartOfAccounts = []
}) => {
  // Normalizar: Si el valor guardado es "services — Servicios", lo tratamos como "services"
  const normalizedValue = React.useMemo(() => {
    const val = localTaxCategory ?? mapRow.tax_category ?? '';
    return val.includes(' — ') ? val.split(' — ')[0] : val;
  }, [localTaxCategory, mapRow.tax_category]);

  const selectedOption = React.useMemo(() => 
    TAX_CATEGORY_OPTIONS.find(o => o.value === normalizedValue) || 
    (normalizedValue ? { value: normalizedValue, label: normalizedValue, group: 'Personalizado' } : null)
  , [normalizedValue]);

  return (
    <TableRow hover selected={isSelected}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(mapRow.id)}
        />
      </TableCell>
      <TableCell sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{mapRow.account_code}</TableCell>
      <TableCell sx={{ fontSize: '0.85rem', color: accountDescription ? 'text.primary' : 'text.disabled' }}>
        {accountDescription || 'Sin descripción'}
      </TableCell>
      <TableCell>
        <Autocomplete
          freeSolo
          size="small"
          options={TAX_CATEGORY_OPTIONS}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          groupBy={(option) => option.group || ''}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.label || option.value || '';
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.value + (option.group || '')}>
              <Typography variant="body2">{option.label}</Typography>
            </li>
          )}
          value={selectedOption}
          onChange={(event, newValue) => {
            const val = typeof newValue === 'string' ? newValue : (newValue?.value || '');
            onCategoryChange(mapRow.id, val);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder="ej: services"
              sx={{ '& .MuiInput-input': { fontSize: '0.85rem' } }}
            />
          )}
          sx={{ minWidth: 180 }}
        />
      </TableCell>
      <TableCell align="center">
        <Checkbox 
          checked={mapRow.applies_vat || false} 
          onChange={(e) => onToggle(mapRow.id, 'applies_vat', e.target.checked)} 
        />
      </TableCell>
      <TableCell align="center">
        <Checkbox 
          checked={mapRow.applies_reteica || false} 
          onChange={(e) => onToggle(mapRow.id, 'applies_reteica', e.target.checked)} 
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          size="small"
          options={chartOfAccounts}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return `${option.account_code} - ${option.description}`;
          }}
          value={chartOfAccounts.find(a => String(a.account_code) === String(mapRow.vat_account_code)) || null}
          onChange={(event, newValue) => {
            onVatAccountChange(mapRow.id, newValue ? String(newValue.account_code) : null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder="Cuenta IVA"
              sx={{ '& .MuiInput-input': { fontSize: '0.85rem' } }}
            />
          )}
          sx={{ minWidth: 200 }}
        />
      </TableCell>
      <TableCell>
        <FormControl size="small" fullWidth>
          <Select
            value={mapRow.retention_concept_id || ''}
            onChange={(e) => onConceptChange(mapRow.id, e.target.value === '' ? null : e.target.value)}
            displayEmpty
          >
            <MenuItem value=""><em>-- Sin ReteFuente --</em></MenuItem>
            {retentionConcepts.map(concept => (
              <MenuItem key={concept.id} value={concept.id}>
                {concept.code} — {concept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
    </TableRow>
  );
});

const FiscalConfigView = ({ fiscalStore }) => {
  const [data, setData] = useState({
    accountTaxMaps: [],
    retentionConcepts: [],
    tenantFiscalConfig: null,
    tenantRetentionAccounts: [],
    chartOfAccounts: [],
    isLoading: true,
    error: null,
  });

  const [formConfig, setFormConfig] = useState({
    default_city_code: '',
    heuristic_iva_exclude_ranges: '',
    responsabilidad_fiscal: 'R-99-PN',
    tenant_nit: '',
    default_expense_account: '',
    allow_cost_accounts: false,
    allow_inventory_accounts: false,
    use_expense_vat_history: true,
    es_responsable_iva: true
  });

  const [savingConfig, setSavingConfig] = useState(false);
  const loadedFiscalConfig = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // New state for adding a retention account
  const [newRetentionAccount, setNewRetentionAccount] = useState({ conceptId: '', accountCode: '' });
  const [isAddingRetention, setIsAddingRetention] = useState(false);
  const [isAutoConfiguring, setIsAutoConfiguring] = useState(false);
  
  // Local state for inline text editing (debounced)
  const [localTaxCategories, setLocalTaxCategories] = useState({});
  const debounceTimers = useRef({});

  // --- Bulk Edit & Search State ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkConfig, setBulkConfig] = useState({
    tax_category: '',
    applies_vat: false,
    applies_reteica: false,
    retention_concept_id: '',
    vat_account_code: ''
  });
  const [isBulkApplying, setIsBulkApplying] = useState(false);
  
  // --- Pagination State ---
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    if (!fiscalStore) return;

    const unsubscribe = fiscalStore.subscribe((state) => {
      setData({
        accountTaxMaps: state.accountTaxMaps,
        retentionConcepts: state.retentionConcepts,
        tenantFiscalConfig: state.tenantFiscalConfig,
        tenantRetentionAccounts: state.tenantRetentionAccounts || [],
        chartOfAccounts: state.chartOfAccounts,
        isLoading: state.isLoading,
        error: state.error,
      });

      if (state.tenantFiscalConfig && state.tenantFiscalConfig !== loadedFiscalConfig.current) {
        loadedFiscalConfig.current = state.tenantFiscalConfig;
        setFormConfig({
          es_gran_contribuyente: state.tenantFiscalConfig.es_gran_contribuyente || false,
          es_responsable_iva: state.tenantFiscalConfig.es_responsable_iva !== false,
          allow_inventory_accounts: state.tenantFiscalConfig.allow_inventory_accounts || false,
          use_expense_vat_history: state.tenantFiscalConfig.use_expense_vat_history !== false,
          allow_cost_accounts: state.tenantFiscalConfig.allow_cost_accounts || false,
          cuenta_proveedores: state.tenantFiscalConfig.cuenta_proveedores || '',
          cuenta_iva_descontable: state.tenantFiscalConfig.cuenta_iva_descontable || '',
          cuenta_reteiva: state.tenantFiscalConfig.cuenta_reteiva || '',
          cuenta_reteica: state.tenantFiscalConfig.cuenta_reteica || '',
          reteiva_rate: state.tenantFiscalConfig.reteiva_rate || 0.15,
          default_city_code: state.tenantFiscalConfig.default_city_code || '',
          heuristic_iva_exclude_ranges: state.tenantFiscalConfig.heuristic_iva_exclude_ranges || '',
          inc_policy: state.tenantFiscalConfig.inc_policy || 'mayor_valor_gasto',
          cuenta_inc_gasto: state.tenantFiscalConfig.cuenta_inc_gasto || '51151002',
          responsabilidad_fiscal: state.tenantFiscalConfig.responsabilidad_fiscal || 'R-99-PN',
          tenant_nit: state.tenantFiscalConfig.tenant_nit || '',
          default_expense_account: state.tenantFiscalConfig.default_expense_account || ''
        });
      }
    });

    fiscalStore.getState().fetchFiscalData();

    return () => {
      unsubscribe();
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, [fiscalStore]);

  // Index account descriptions and retention concepts for O(1) lookup
  const accountsMap = React.useMemo(() => {
    const map = {};
    data.chartOfAccounts.forEach(a => {
      map[String(a.account_code)] = a.description;
    });
    return map;
  }, [data.chartOfAccounts]);

  const conceptsMap = React.useMemo(() => {
    const map = {};
    data.retentionConcepts.forEach(c => {
      map[c.id] = `${c.code} — ${c.name}`;
    });
    return map;
  }, [data.retentionConcepts]);
  
  // Priorizar cuentas de IVA (2408) y descripciones con "IVA"
  const sortedChartOfAccounts = React.useMemo(() => {
    return [...data.chartOfAccounts].sort((a, b) => {
      const aCode = String(a.account_code);
      const bCode = String(b.account_code);
      const aIsIva = aCode.startsWith('2408') || /IVA/i.test(a.description || '');
      const bIsIva = bCode.startsWith('2408') || /IVA/i.test(b.description || '');
      
      if (aIsIva && !bIsIva) return -1;
      if (!aIsIva && bIsIva) return 1;
      
      // Si ambos son o no son IVA, ordenar por código
      return aCode.localeCompare(bCode);
    });
  }, [data.chartOfAccounts]);

  // Initialize local tax categories from data
  useEffect(() => {
    if (data.accountTaxMaps.length > 0) {
      const initial = {};
      data.accountTaxMaps.forEach(row => {
        if (localTaxCategories[row.id] === undefined) {
          initial[row.id] = row.tax_category || '';
        }
      });
      if (Object.keys(initial).length > 0) {
        setLocalTaxCategories(prev => ({ ...initial, ...prev }));
      }
    }
  }, [data.accountTaxMaps]);

  const handleConfigChange = React.useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const saveFiscalConfig = async () => {
    setSavingConfig(true);
    const result = await fiscalStore.getState().saveTenantFiscalConfig({
      es_gran_contribuyente: formConfig.es_gran_contribuyente,
      es_responsable_iva: formConfig.es_responsable_iva,
      allow_inventory_accounts: formConfig.allow_inventory_accounts,
      use_expense_vat_history: formConfig.use_expense_vat_history !== false,
      allow_cost_accounts: formConfig.allow_cost_accounts,
      cuenta_proveedores: formConfig.cuenta_proveedores,
      cuenta_iva_descontable: formConfig.cuenta_iva_descontable,
      cuenta_reteiva: formConfig.cuenta_reteiva,
      cuenta_reteica: formConfig.cuenta_reteica,
      reteiva_rate: parseFloat(formConfig.reteiva_rate),
      default_city_code: formConfig.default_city_code,
      heuristic_iva_exclude_ranges: formConfig.heuristic_iva_exclude_ranges,
      inc_policy: formConfig.inc_policy,
      cuenta_inc_gasto: formConfig.cuenta_inc_gasto,
      responsabilidad_fiscal: formConfig.responsabilidad_fiscal,
      tenant_nit: formConfig.tenant_nit,
      default_expense_account: formConfig.default_expense_account
    });
    setSavingConfig(false);
    setSnackbar({
      open: true,
      message: result?.success ? '✅ Configuración guardada' : '❌ Error al guardar',
      severity: result?.success ? 'success' : 'error'
    });
  };

  const handleAddRetentionAccount = async () => {
    if (!newRetentionAccount.conceptId || !newRetentionAccount.accountCode) return;
    setIsAddingRetention(true);
    const result = await fiscalStore.getState().saveTenantRetentionAccount(
      newRetentionAccount.conceptId,
      newRetentionAccount.accountCode
    );
    setIsAddingRetention(false);
    if (result.success) {
      setNewRetentionAccount({ conceptId: '', accountCode: '' });
      setSnackbar({ open: true, message: '✅ Mapeo de retención agregado', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: '❌ Error al agregar mapeo', severity: 'error' });
    }
  };

  const handleDeleteRetentionAccount = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este mapeo de retención?')) return;
    const result = await fiscalStore.getState().deleteTenantRetentionAccount(id);
    if (result.success) {
      setSnackbar({ open: true, message: '✅ Mapeo eliminado', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: '❌ Error al eliminar', severity: 'error' });
    }
  };

  const handleHeuristicAutoMap = async (overwriteAll = false) => {
    if (!overwriteAll && !window.confirm('¿Desea que el sistema sugiera categorías e impuestos basados en los nombres de las cuentas?\n\n(Solo se completarán los espacios vacíos)')) return;
    
    setIsAutoConfiguring(true);
    const result = await fiscalStore.getState().heuristicAutoMap(formConfig, overwriteAll);
    setIsAutoConfiguring(false);

    if (result.success) {
      let msg = '✅ Sugerencias aplicadas: ';
      if (result.count > 0) msg += `${result.count} mapeos de gasto; `;
      if (result.discovered > 0) msg += `${result.discovered} cuentas de retención descubiertas; `;
      if (result.count === 0 && result.discovered === 0) msg = 'ℹ️ No se encontraron nuevos mapeos sugeridos.';
      
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } else {
      setSnackbar({ open: true, message: `❌ Error: ${result.error || result.message}`, severity: 'error' });
    }
  };

  // Immediate save for checkboxes and selects
  const handleTaxMapToggle = React.useCallback(async (id, field, value) => {
    await fiscalStore.getState().updateAccountTaxMap(id, { [field]: value });
  }, [fiscalStore]);

  const handleTaxMapConceptChange = React.useCallback(async (id, conceptId) => {
    await fiscalStore.getState().updateAccountTaxMap(id, { retention_concept_id: conceptId });
  }, [fiscalStore]);

  // Debounced save for text fields (tax_category)
  const handleTaxCategoryChange = React.useCallback((id, value) => {
    setLocalTaxCategories(prev => ({ ...prev, [id]: value }));

    if (debounceTimers.current[id]) {
      clearTimeout(debounceTimers.current[id]);
    }

    debounceTimers.current[id] = setTimeout(async () => {
      await fiscalStore.getState().updateAccountTaxMap(id, { tax_category: value });
    }, 800);
  }, [fiscalStore]);

  const handleVatAccountChange = React.useCallback(async (id, accountCode) => {
    await fiscalStore.getState().updateAccountTaxMap(id, { vat_account_code: accountCode });
  }, [fiscalStore]);

  // --- Filter & Pagination Logic ---
  const filteredMaps = React.useMemo(() => {
    const term = searchTerm.toLowerCase();
    return data.accountTaxMaps.filter(mapRow => {
      const desc = accountsMap[String(mapRow.account_code)] || '';
      return mapRow.account_code.toString().includes(term) || 
             desc.toLowerCase().includes(term);
    });
  }, [data.accountTaxMaps, accountsMap, searchTerm]);
  
  const pagedMaps = React.useMemo(() => {
    return filteredMaps.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredMaps, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page on search
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredMaps.map(m => m.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = React.useCallback((id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);

  const applyBulkChanges = async () => {
    if (selectedIds.length === 0) return;
    setIsBulkApplying(true);
    
    const dataToUpdate = {};
    if (bulkConfig.tax_category) dataToUpdate.tax_category = bulkConfig.tax_category;
    if (bulkConfig.retention_concept_id !== '') dataToUpdate.retention_concept_id = bulkConfig.retention_concept_id === 'null' ? null : bulkConfig.retention_concept_id;
    
    dataToUpdate.applies_vat = bulkConfig.applies_vat;
    dataToUpdate.applies_reteica = bulkConfig.applies_reteica;
    if (bulkConfig.vat_account_code) dataToUpdate.vat_account_code = bulkConfig.vat_account_code;

    const result = await fiscalStore.getState().bulkUpdateAccountTaxMap(selectedIds, dataToUpdate);
    
    setIsBulkApplying(false);
    if (result.success) {
      setSnackbar({ open: true, message: `✅ Aplicado a ${selectedIds.length} cuentas`, severity: 'success' });
      setSelectedIds([]);
    } else {
      setSnackbar({ open: true, message: '❌ Error al aplicar cambios masivos', severity: 'error' });
    }
  };

  if (data.isLoading && data.accountTaxMaps.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (data.error) return <Alert severity="error">{data.error}</Alert>;

  return (
    <Box sx={{ pb: 6 }}>
      <Typography variant="h5" color="primary" sx={{ mb: 3, fontWeight: "bold" }}>
        Configuración Fiscal y Mapeo Tributario
      </Typography>

      {/* Panel de Configuración Global */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Parámetros Generales de la Empresa (Tenant)</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Si se activa, el sistema podrá practicar ReteIVA (ej. a proveedores Régimen Simple o generales, según su tarifa)" arrow>
              <FormControlLabel
                control={<Switch checked={formConfig.es_responsable_iva !== false} onChange={handleConfigChange} name="es_responsable_iva" color="primary" />}
                label="Es Responsable de IVA"
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
             {/* Es gran contribuyente quedó obsoleto con responsabilidad fiscal, pero se mantiene por retrocompatibilidad temporal */}
             <FormControlLabel
              control={<Switch checked={formConfig.es_gran_contribuyente} onChange={handleConfigChange} name="es_gran_contribuyente" />}
              label="Es Gran Contribuyente"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Si se activa, el modelo de IA y el clasificador también analizarán y clasificarán utilizando cuentas de inventario (Clase 14)" arrow>
              <FormControlLabel
                control={<Switch checked={formConfig.allow_inventory_accounts || false} onChange={handleConfigChange} name="allow_inventory_accounts" />}
                label="Usar Cuentas de Inventario (14)"
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Permite que el clasificador utilice cuentas de costo de las clases 6 y 7 en el historial y el plan contable" arrow>
              <FormControlLabel
                control={<Switch checked={formConfig.allow_cost_accounts || false} onChange={handleConfigChange} name="allow_cost_accounts" />}
                label="Usar Cuentas de Costo (6 y 7)"
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={<Switch checked={formConfig.use_expense_vat_history !== false} onChange={handleConfigChange} name="use_expense_vat_history" disabled={savingConfig} />}
              label="Buscar IVA en el historial de gastos"
            />
            <Typography variant="caption" color="text.secondary" component="p">
              Conserva la búsqueda de IVA en ejemplos de gastos. Al desactivarlo, usa el historial específico de impuestos del ERP. Los ejemplos manuales tienen prioridad.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="NIT de la Empresa (Tenant)" name="tenant_nit" value={formConfig.tenant_nit} onChange={handleConfigChange} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <Select
                name="responsabilidad_fiscal"
                value={formConfig.responsabilidad_fiscal || 'R-99-PN'}
                onChange={handleConfigChange}
                displayEmpty
              >
                <MenuItem value="R-99-PN">R-99-PN (Régimen Común/Predeterminado)</MenuItem>
                <MenuItem value="O-13">O-13 (Gran Contribuyente)</MenuItem>
                <MenuItem value="O-15">O-15 (Autorretenedor)</MenuItem>
                <MenuItem value="O-23">O-23 (Agente Retenedor IVA)</MenuItem>
                <MenuItem value="O-47">O-47 (Régimen Simple)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Cuenta Proveedores (CXP Base)" name="cuenta_proveedores" value={formConfig.cuenta_proveedores} onChange={handleConfigChange} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Cuenta IVA Descontable" name="cuenta_iva_descontable" value={formConfig.cuenta_iva_descontable} onChange={handleConfigChange} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Cuenta ReteIVA" name="cuenta_reteiva" value={formConfig.cuenta_reteiva} onChange={handleConfigChange} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Cuenta ReteICA" name="cuenta_reteica" value={formConfig.cuenta_reteica} onChange={handleConfigChange} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <Select
                name="inc_policy"
                value={formConfig.inc_policy || 'mayor_valor_gasto'}
                onChange={handleConfigChange}
                displayEmpty
              >
                <MenuItem value="mayor_valor_gasto">INC: Sumar al Gasto</MenuItem>
                <MenuItem value="cuenta_separada">INC: Cuenta Separada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {formConfig.inc_policy === 'cuenta_separada' && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Cuenta Gasto INC (8%)" name="cuenta_inc_gasto" value={formConfig.cuenta_inc_gasto} onChange={handleConfigChange} size="small" />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Tasa ReteIVA (ej. 0.15)" type="number" inputProps={{ step: "0.01" }} name="reteiva_rate" value={formConfig.reteiva_rate} onChange={handleConfigChange} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Cód. Ciudad Defecto DANE" name="default_city_code" value={formConfig.default_city_code} onChange={handleConfigChange} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Autocomplete
              size="small"
              options={data.chartOfAccounts.filter(account => {
                const code = String(account.account_code);
                return code.startsWith('5') ||
                  (formConfig.allow_cost_accounts && (code.startsWith('6') || code.startsWith('7'))) ||
                  (formConfig.allow_inventory_accounts && code.startsWith('14'));
              })}
              getOptionLabel={(option) => `${option.account_code} - ${option.description || 'Sin descripción'}`}
              value={data.chartOfAccounts.find(account => String(account.account_code) === String(formConfig.default_expense_account)) || null}
              onChange={(event, option) => setFormConfig(prev => ({
                ...prev,
                default_expense_account: option ? String(option.account_code) : ''
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cuenta fallback de gasto/costo"
                  helperText="Se usa únicamente cuando historial, IA y plan contable no producen una cuenta válida."
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField 
              fullWidth 
              label="Rangos de IVA a ignorar en sugerencias (opcional)" 
              placeholder="ej: 240801, 240805-240810"
              name="heuristic_iva_exclude_ranges" 
              value={formConfig.heuristic_iva_exclude_ranges} 
              onChange={handleConfigChange} 
              size="small" 
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="Si ingresas un rango, el botón 'Sugerir Configuración' evitará poner esas cuentas como IVA. Útil para ignorar la cuenta de IVA General." arrow>
                      <InfoOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="flex-end">
             <Button variant="contained" startIcon={<SaveIcon />} onClick={saveFiscalConfig} disabled={savingConfig}>
               {savingConfig ? 'Guardando...' : 'Guardar Configuración'}
             </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Panel de Cuentas de Retención (Ahora Colapsable) */}
      <Accordion sx={{ mb: 4 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Cuentas Contables de Retención</Typography>
            <Tooltip title="Estas cuentas le dicen al motor fiscal: 'Cuando apliques retención por concepto X, registra el crédito en la cuenta Y'. Se sincronizan automáticamente desde el ERP." arrow>
              <InfoOutlinedIcon fontSize="small" color="action" onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Mapeo entre cada concepto de retención y la cuenta contable donde se registra el crédito.
            Use esta sección para asignar cuentas a conceptos globales (ej: Servicios) o corregir mapeos sincronizados.
          </Typography>

          <Box sx={{ mb: 3, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.02)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Agregar o Actualizar Mapeo Manual:</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <FormControl size="small" fullWidth>
                  <Select
                    value={newRetentionAccount.conceptId}
                    onChange={(e) => setNewRetentionAccount(prev => ({ ...prev, conceptId: e.target.value }))}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Seleccionar Concepto...</MenuItem>
                    {data.retentionConcepts.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Autocomplete
                  size="small"
                  options={data.chartOfAccounts}
                  getOptionLabel={(option) => `${option.account_code} - ${option.description}`}
                  onChange={(e, val) => setNewRetentionAccount(prev => ({ ...prev, accountCode: val ? val.account_code : '' }))}
                  renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Buscar cuenta contable..." />}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={handleAddRetentionAccount} 
                  disabled={isAddingRetention || !newRetentionAccount.conceptId || !newRetentionAccount.accountCode}
                >
                  Asignar
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table size="small" sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Concepto de Retención</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cuenta Contable (Crédito)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Descripción Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 80 }} align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.tenantRetentionAccounts.map((row) => {
                  const conceptName = conceptsMap[row.retention_concept_id];
                  const accountDesc = accountsMap[String(row.account_code)];
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {conceptName || `Concepto #${row.retention_concept_id}`}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {row.account_code}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', color: accountDesc ? 'text.primary' : 'text.disabled' }}>
                        {accountDesc || 'Sin descripción'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleDeleteRetentionAccount(row.id)}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Panel de Mapeo de Cuentas de Gasto */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Mapeo Fiscal de Cuentas de Gasto</Typography>
            <Typography variant="body2" color="text.secondary">
              Defina qué impuestos aplica cada cuenta de gasto/activo.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200, mr: 1 }}>
              <Select
                value={isAutoConfiguring ? 'detecting' : 'normal'}
                onChange={(e) => {
                  if(e.target.value === 'overwrite') {
                    if(window.confirm('⚠️ MODO SOBRESCRITURA: Esto borrará el mapeo fiscal actual (IVA, ReteICA, Categorías y ReteFuente) para reinstalar sugerencias basadas en historial y reglas.\n\n¿Desea continuar?')) {
                      handleHeuristicAutoMap(true);
                    }
                  } else if (e.target.value === 'normal') {
                    // Do nothing on select, the button handles this.
                  }
                }}
                disabled={isAutoConfiguring}
                displayEmpty
              >
                <MenuItem value="normal" disabled={true} sx={{display: 'none'}}>Sugerir Configuración</MenuItem>
                <MenuItem value="detecting" disabled={true} sx={{display: 'none'}}>Detectando...</MenuItem>
                <MenuItem value="overwrite" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  Autoconfigurar (Sobrescribir Todo)
                </MenuItem>
              </Select>
            </FormControl>

            <Button 
                variant="contained" 
                color="secondary"
                startIcon={<AutoFixHighIcon />}
                onClick={() => handleHeuristicAutoMap(false)}
                disabled={isAutoConfiguring}
                size="small"
              >
                {isAutoConfiguring ? 'Detectando...' : 'Sugerir Configuración (Rellenar)'}
            </Button>
            <TextField
              size="small"
              placeholder="Buscar por código o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </Box>

        {/* Barra de Acciones Masivas (Sticky at the bottom) */}
        {selectedIds.length > 0 && (
          <Paper 
            elevation={10} 
            sx={{ 
              position: 'fixed', 
              bottom: 20, 
              left: '50%', 
              transform: 'translateX(-50%)',
              p: 2, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flexWrap: 'wrap',
              zIndex: 1300,
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: 4,
              minWidth: '80%',
              boxShadow: '0px 8px 30px rgba(0,0,0,0.3)',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            <style>
              {`
                @keyframes slideUp {
                  from { transform: translate(-50%, 100%); opacity: 0; }
                  to { transform: translate(-50%, 0); opacity: 1; }
                }
              `}
            </style>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', px: 2 }}>
              {selectedIds.length} seleccionadas
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            
            <Autocomplete
              freeSolo
              size="small"
              options={TAX_CATEGORY_OPTIONS}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              groupBy={(option) => option.group || ''}
              getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || '')}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Categoría ICA" 
                  variant="filled"
                  sx={{ 
                    width: 200, 
                    backgroundColor: 'rgba(255,255,255,0.9)', 
                    borderRadius: 1,
                    '& .MuiInputBase-root': { height: 40 }
                  }} 
                />
              )}
              onChange={(e, val) => setBulkConfig(prev => ({ ...prev, tax_category: typeof val === 'string' ? val : (val?.value || '') }))}
            />

            <FormControlLabel
              control={<Checkbox size="small" checked={bulkConfig.applies_vat} onChange={(e) => setBulkConfig(prev => ({ ...prev, applies_vat: e.target.checked }))} sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }} />}
              label="IVA"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={bulkConfig.applies_reteica} onChange={(e) => setBulkConfig(prev => ({ ...prev, applies_reteica: e.target.checked }))} sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }} />}
              label="ReteICA"
            />

            <Autocomplete
              size="small"
              options={sortedChartOfAccounts}
              getOptionLabel={(option) => typeof option === 'string' ? option : `${option.account_code} - ${option.description}`}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Cuenta IVA" 
                  variant="filled"
                  sx={{ 
                    width: 220, 
                    backgroundColor: 'rgba(255,255,255,0.9)', 
                    borderRadius: 1,
                    '& .MuiInputBase-root': { height: 40 }
                  }} 
                />
              )}
              onChange={(e, val) => setBulkConfig(prev => ({ ...prev, vat_account_code: val ? String(val.account_code) : '' }))}
            />

            <FormControl variant="filled" size="small" sx={{ width: 220, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
              <Select
                value={bulkConfig.retention_concept_id}
                onChange={(e) => setBulkConfig(prev => ({ ...prev, retention_concept_id: e.target.value }))}
                displayEmpty
                sx={{ height: 40 }}
              >
                <MenuItem value=""><em>-- Sin cambios ReteFuente --</em></MenuItem>
                <MenuItem value="null">-- Quitar ReteFuente --</MenuItem>
                {data.retentionConcepts.map(concept => (
                  <MenuItem key={concept.id} value={concept.id}>
                    {concept.code} — {concept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <Button 
              variant="contained" 
              color="secondary" 
              startIcon={<CheckCircleIcon />} 
              onClick={applyBulkChanges}
              disabled={isBulkApplying}
              sx={{ fontWeight: 'bold' }}
            >
              {isBulkApplying ? 'Aplicando...' : 'Aplicar Cambios'}
            </Button>
            
            <IconButton color="inherit" onClick={() => setSelectedIds([])} size="small">
              <ClearIcon />
            </IconButton>
          </Paper>
        )}

        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < filteredMaps.length}
                    checked={filteredMaps.length > 0 && selectedIds.length === filteredMaps.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cuenta</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>
                  <Tooltip title="Seleccione una categoría predefinida o escriba un código CIIU numérico (ej: 4631). Se usa para buscar la tarifa de ICA del municipio." arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Categoría (ICA)
                      <InfoOutlinedIcon fontSize="inherit" color="action" />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>IVA</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>ReteICA</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cuenta IVA (Específica)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Concepto Retención (ReteFuente)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedMaps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {searchTerm ? 'No se encontraron cuentas que coincidan con la búsqueda.' : 'No hay mapeos fiscales inicializados. Sincronice el PUC primero.'}
                  </TableCell>
                </TableRow>
              ) : (
                pagedMaps.map((mapRow) => (
                  <FiscalRow
                    key={mapRow.id}
                    mapRow={mapRow}
                    accountDescription={accountsMap[String(mapRow.account_code)]}
                    retentionConcepts={data.retentionConcepts}
                    localTaxCategory={localTaxCategories[mapRow.id]}
                    isSelected={selectedIds.includes(mapRow.id)}
                    onSelect={handleSelectRow}
                    onToggle={handleTaxMapToggle}
                    onConceptChange={handleTaxMapConceptChange}
                    onCategoryChange={handleTaxCategoryChange}
                    onVatAccountChange={handleVatAccountChange}
                    chartOfAccounts={sortedChartOfAccounts}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 250]}
          component="div"
          count={filteredMaps.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Cuentas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
};

export default FiscalConfigView;
